/**
 * References Store - State Management для справочников
 * Аналог lib/features/references/presentation/bloc/references_bloc.dart
 *
 * Загружаются один раз при старте приложения и кешируются
 */
import { defineStore } from 'pinia'
import { ref, computed, type Ref } from 'vue'
import { referencesApi } from '../api/references.api'
import type {
  LeadSource,
  Country,
  CitySearchResult,
  ArchiveReason,
  ClientTagReference,
  ClientFieldDefinition,
} from '../types/references.types'

/** Общая часть слайса — то, чем стор управляет одинаково для всех справочников. */
interface ReferenceSliceState {
  isLoading: Ref<boolean>
  isLoaded: Ref<boolean>
  error: Ref<string | null>
  load: (force?: boolean) => Promise<void>
}

interface ReferenceSlice<T> extends ReferenceSliceState {
  items: Ref<T[]>
}

/**
 * Один справочник: свои данные, свой флаг загрузки, своя ошибка.
 * Падение одного не должно оставлять пустыми остальные три (DEV-1801).
 */
function createReferenceSlice<T>(
  referenceName: string,
  loader: () => Promise<T[]>,
): ReferenceSlice<T> {
  const items = ref([]) as Ref<T[]>
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<string | null>(null)

  // Монотонный счётчик, как в useCitySearch: применяем ответ только последнего запроса.
  let requestId = 0
  // Один запрос на справочник, даже если loadAll позвали из двух компонентов сразу.
  let inFlight: Promise<void> | null = null

  // async-обёртка: синхронный throw из loader'а тоже станет отказом промиса,
  // а не разорвёт map() в loadAll до Promise.allSettled.
  const runLoad = async (currentRequestId: number): Promise<void> => {
    try {
      const loadedItems = await loader()
      if (currentRequestId !== requestId) {
        // Нас обогнал force-запрос: ждём его, иначе вызывающий получит управление
        // раньше данных — с пустым справочником и без ошибки.
        await inFlight
        return
      }
      if (!Array.isArray(loadedItems)) {
        throw new Error('Reference response is not a list')
      }
      items.value = loadedItems
      isLoaded.value = true
      error.value = null
    } catch (err: unknown) {
      if (currentRequestId !== requestId) {
        await inFlight
        return
      }
      // Наружу не бросаем и данные не затираем: остальные справочники должны выжить.
      isLoaded.value = false
      error.value = err instanceof Error && err.message ? err.message : 'Failed to load references'
      console.error(`Failed to load reference "${referenceName}":`, err)
    } finally {
      if (currentRequestId === requestId) {
        isLoading.value = false
      }
    }
  }

  const load = (force = false): Promise<void> => {
    // Успешно загруженный не перезапрашиваем; после ошибки следующий вызов — уже ретрай.
    if (isLoaded.value && error.value === null && !force) {
      return Promise.resolve()
    }
    // Обычный вызов подхватывает идущий запрос, force всегда идёт в сеть заново.
    if (inFlight && !force) {
      return inFlight
    }

    // Между requestId и inFlight не должно быть await: иначе обогнанный запрос
    // дождётся собственного промиса и подвесит всех, кто ждёт loadAll.
    requestId += 1
    isLoading.value = true
    error.value = null

    const request = runLoad(requestId)
    inFlight = request
    // Свой запрос снимает только сам себя: force мог уже поставить следующий.
    void request.finally(() => {
      if (inFlight === request) {
        inFlight = null
      }
    })

    return request
  }

  return { items, isLoading, isLoaded, error, load }
}

export const useReferencesStore = defineStore('references', () => {
  // State
  // DEV-42: clientStatuses упразднён вместе со справочником.
  // DEV-232: partners упразднён — атрибуция клиента через clients.agent_id.
  // DEV-852: getCities больше не грузим — города клиента ищет useCitySearch.
  // DEV-1801: четыре независимых слайса вместо одного Promise.all на всех.
  const leadSourcesSlice = createReferenceSlice<LeadSource>('leadSources', () =>
    referencesApi.getLeadSources(),
  )
  const countriesSlice = createReferenceSlice<Country>('countries', () =>
    referencesApi.getCountries(),
  )
  // DEV-1061/DEV-1062: причины архивации и теги клиента.
  const archiveReasonsSlice = createReferenceSlice<ArchiveReason>('archiveReasons', () =>
    referencesApi.getArchiveReasons(),
  )
  const clientTagsSlice = createReferenceSlice<ClientTagReference>('clientTags', () =>
    referencesApi.getTags(),
  )

  // DEV-2069. Реестр полей карточки нужен двум экранам — конструктору воронки и
  // карточке сделки, — поэтому в общий loadAll не входит: остальным он не нужен,
  // а лишний запрос на каждом старте приложения ничего не окупает.
  const clientFieldsSlice = createReferenceSlice<ClientFieldDefinition>('clientFields', () =>
    referencesApi.getClientFieldDefinitions(),
  )

  const allSlices: ReferenceSliceState[] = [
    leadSourcesSlice,
    countriesSlice,
    archiveReasonsSlice,
    clientTagsSlice,
  ]

  const leadSources = leadSourcesSlice.items
  const countries = countriesSlice.items
  const archiveReasons = archiveReasonsSlice.items
  const clientTags = clientTagsSlice.items
  const clientFields = clientFieldsSlice.items

  const isLoading = computed(() => allSlices.some((slice) => slice.isLoading.value))
  const isLoaded = computed(() => allSlices.every((slice) => slice.isLoaded.value))
  /** Первая непустая ошибка — для тех, кому достаточно «что-то не загрузилось». */
  const error = computed(
    () => allSlices.find((slice) => slice.error.value !== null)?.error.value ?? null,
  )

  // Пер-справочные состояния: UI показывает ошибку и «Повторить» только у своего поля.
  // Наружу торчат только те справочники, у которых такое поле есть — источники и страны.
  const leadSourcesError = computed(() => leadSourcesSlice.error.value)
  const countriesError = computed(() => countriesSlice.error.value)
  const isLeadSourcesLoading = computed(() => leadSourcesSlice.isLoading.value)
  const isCountriesLoading = computed(() => countriesSlice.isLoading.value)

  // Getters
  // DEV-42: activeClientStatuses удалён.

  /** Активные источники лидов (отсортированные) */
  const activeLeadSources = computed(() =>
    leadSources.value.filter((source) => source.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
  )

  /** Все страны (для селектов и форм) */
  const allCountries = computed(() => countries.value)

  // Actions

  /**
   * Загрузить все справочники
   * Вызывается один раз при инициализации приложения.
   * Наружу не бросает: упавший справочник оставляет свою ошибку, остальные наполняются.
   */
  const loadAll = async () => {
    await Promise.allSettled(allSlices.map((slice) => slice.load()))
  }

  /**
   * Перезагрузить все справочники (force refresh)
   */
  const reload = async () => {
    await Promise.allSettled(allSlices.map((slice) => slice.load(true)))
  }

  // Ретрай одного справочника — для кнопки «Повторить» в поле формы.
  const loadLeadSources = () => leadSourcesSlice.load()
  const loadCountries = () => countriesSlice.load()

  // DEV-2069. Ленивая загрузка реестра полей: повторный вызов не ходит в сеть.
  const loadClientFields = (force = false) => clientFieldsSlice.load(force)

  // DEV-42: getClientStatusById / getClientStatusByKey удалены.

  /**
   * Получить источник лида по ID
   */
  const getLeadSourceById = (id: string): LeadSource | undefined => {
    return leadSources.value.find((source) => source.id === id)
  }

  /**
   * Получить источник лида по ключу
   */
  const getLeadSourceByKey = (key: string): LeadSource | undefined => {
    return leadSources.value.find((source) => source.key === key)
  }

  /**
   * Получить страну по ID
   */
  const getCountryById = (id: string): Country | undefined => {
    return countries.value.find((country) => country.id === id)
  }

  /**
   * Получить страну по ISO коду
   */
  const getCountryByIsoCode = (isoCode: string): Country | undefined => {
    return countries.value.find(
      (country) => country.isoCode.toLowerCase() === isoCode.toLowerCase(),
    )
  }

  /**
   * Поиск городов для клиентских форм (DEV-850).
   * Без кеша и без записи в state: результат зависит от запроса, живёт в useCitySearch.
   */
  const searchCities = (
    query: string,
    countryId?: string,
    limit?: number,
  ): Promise<CitySearchResult[]> => referencesApi.searchCities(query, countryId, limit)

  /** Причина архивации по ключу — для подписи «нет диалога» и фильтров (DEV-1061). */
  const getArchiveReasonByKey = (key: string): ArchiveReason | undefined =>
    archiveReasons.value.find((reason) => reason.key === key)

  return {
    // State
    leadSources,
    countries,
    archiveReasons,
    clientTags,
    clientFields,
    isLoading,
    isLoaded,
    error,

    // DEV-1801: состояние справочников, у которых в форме своя кнопка «Повторить».
    leadSourcesError,
    countriesError,
    isLeadSourcesLoading,
    isCountriesLoading,

    // Getters
    activeLeadSources,
    allCountries,

    // Actions
    loadAll,
    reload,
    loadLeadSources,
    loadCountries,
    loadClientFields,
    getLeadSourceById,
    getLeadSourceByKey,
    getCountryById,
    getCountryByIsoCode,
    searchCities,
    getArchiveReasonByKey,
  }
})
